import type { Vec3 } from '../vec';

// Contents bits used by the player mask (same values as Source's bspflags.h).
export const CONTENTS_SOLID = 0x1;
export const CONTENTS_WINDOW = 0x2;
export const CONTENTS_GRATE = 0x8;
export const CONTENTS_PLAYERCLIP = 0x10000;
export const CONTENTS_MONSTER = 0x2000000;
export const CONTENTS_LADDER = 0x20000000;
export const CONTENTS_SLIME = 0x10;
export const CONTENTS_WATER = 0x20;
export const MASK_WATER = CONTENTS_SLIME | CONTENTS_WATER;
/** What bullets and the knife hit (Source MASK_SOLID). */
export const MASK_SOLID = CONTENTS_SOLID | CONTENTS_WINDOW | CONTENTS_GRATE | 0x4000 | CONTENTS_MONSTER;
export const MASK_PLAYERSOLID =
  CONTENTS_SOLID | CONTENTS_WINDOW | CONTENTS_GRATE | CONTENTS_PLAYERCLIP | CONTENTS_MONSTER;

/**
 * Something solid the player can touch. Game rules hang their data off it
 * (bhop block state, checkpoint spawn). Worlds may extend it.
 */
export interface SolidEntity {
  contents: number;
  /** Set when standing on this entity is subject to the bhop block contact rule. */
  block?: import('../bhopBlocks').BhopBlock;
  /** Set when landing on this entity makes it the current checkpoint. */
  checkpoint?: import('../player').SpawnPoint;
  /** Booster pad: velocity added when the player jumps off it. */
  boost?: import('../vec').Vec3;
  /** BspWorld: index of the solid (0 = world) this entity stands for. */
  solidIndex?: number;
  classname?: string;
}

export class TraceResult {
  /** 0..1 of the way from start to end. 1 means nothing was hit. */
  fraction = 1;
  endPos: Vec3 = [0, 0, 0];
  /** Valid only when fraction < 1. */
  planeNormal: Vec3 = [0, 0, 0];
  startSolid = false;
  allSolid = false;
  entity: SolidEntity | null = null;
  /** Contents bits of the brush that was hit (0 when nothing was hit). */
  contents = 0;
}

/** Collision interface used by movement. Implemented by BoxWorld (built-in course) and later BspWorld. */
export interface CollisionWorld {
  /** True if some brush has CONTENTS_LADDER; movement only looks for ladders then. */
  readonly hasLadders?: boolean;
  /** True if some brush has water (or slime); movement only checks for swimming then. */
  readonly hasWater?: boolean;
  /** Sweep the box [mins, maxs] (relative to the origin) from start to end. Writes into `out` and returns it. */
  trace(
    start: Readonly<Vec3>,
    end: Readonly<Vec3>,
    mins: Readonly<Vec3>,
    maxs: Readonly<Vec3>,
    mask: number,
    out: TraceResult,
  ): TraceResult;
  /** True if the box at `origin` overlaps anything solid. */
  testPosition(origin: Readonly<Vec3>, mins: Readonly<Vec3>, maxs: Readonly<Vec3>, mask: number): boolean;
}
